// LiveVoiceButton — header toggle for the Gemini 3.1 Flash Live audio-to-audio
// session. Only rendered when `permission_live_voice` is enabled.
//
// The `live` state is owned by the parent (`AuthedApp`) so that the
// transcript panel can share it. The parent passes the active chat
// conversationId down so Live turns join the existing thread.

import type { UseLiveVoiceResult } from '../hooks/useLiveVoice';

function WaveformIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M4 10v4h2v-4H4zm4-4v12h2V6H8zm4 2v8h2V8h-2zm4-3v14h2V5h-2zm4 5v4h2v-4h-2z"
      />
    </svg>
  );
}

function SpinnerIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
      className="mic-spinner"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeDasharray="14 28"
      />
    </svg>
  );
}

export function LiveVoiceButton({
  live,
  conversationId,
}: {
  live: UseLiveVoiceResult;
  conversationId: string | null;
}): JSX.Element {
  const handleClick = (): void => {
    if (live.state === 'idle' || live.state === 'error') {
      void live.start({ conversationId });
    } else {
      live.stop();
    }
  };

  const isConnecting = live.state === 'connecting';
  const isActive =
    live.state === 'listening' ||
    live.state === 'speaking' ||
    live.state === 'ready';

  const label = isActive
    ? 'Stop live voice'
    : isConnecting
      ? 'Connecting live voice…'
      : 'Start live voice';

  const className =
    'live-voice-btn' +
    (isActive ? ' live-voice-btn-active' : '') +
    (isConnecting ? ' live-voice-btn-connecting' : '') +
    (live.state === 'error' ? ' live-voice-btn-error' : '');

  const pillText =
    live.toolActive
      ? 'using tools…'
      : live.state === 'speaking'
        ? 'speaking'
        : live.state === 'listening'
          ? 'listening'
          : null;

  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={label}
        title={live.error ? `Live voice error: ${live.error}` : label}
        onClick={handleClick}
        disabled={isConnecting}
      >
        {isConnecting ? <SpinnerIcon /> : <WaveformIcon />}
      </button>
      {isActive && pillText ? (
        <span className="live-voice-pill" aria-live="polite">
          {pillText}
        </span>
      ) : null}
    </>
  );
}
